// src/services/intentClassifier.ts
// Layered Intent Classification System for Xiaofeng OS Dialogue Engine
// Architecture: Rule Engine (5ms) → Embedding Classifier (30ms) → LLM Fallback (200ms+)
import { env } from '@xenova/transformers';

// Suppress model downloads in production - only first-time download needed
env.allowLocalModels = false;
env.allowRemoteModels = true;

/**
 * Core intent types supported by the system
 */
export type IntentType = 'chat' | 'task' | 'question' | 'creative' | 'command';

/**
 * Subtype mapping for each intent type
 */
export interface SubtypeMap {
  chat: 'casual' | 'greeting' | 'smalltalk';
  task: 'planning' | 'research' | 'analysis' | 'organization';
  question: 'fact' | 'how-to' | 'explanation';
  creative: 'writing' | 'design' | 'brainstorm' | 'generation';
  command: 'system' | 'tool' | 'action';
}

/**
 * Priority levels for response urgency
 */
export type Priority = 'urgent' | 'high' | 'normal';

/**
 * Entity extracted from user input
 */
export interface Entity {
  name: string;        // Entity type/name
  value?: string;      // Entity value
  confidence?: number; // Extraction confidence
}

/**
 * Main classification result interface
 */
export interface IntentClassification {
  id: string;                          // Request UUID
  originalText: string;                // Original user input (required for logging/audit)
  intent: IntentType;                  // Primary intent
  subtype: SubtypeMap[IntentType];     // Specific subtype
  confidence: number;                  // Confidence score 0-1
  reasoning: string;                   // Why this classification
  suggestedModel: ModelType;           // Recommended LLM model
  priority: Priority;                  // Response priority
  entities: Entity[];                  // Extracted entities
  requiresTools: string[];             // Tool names to invoke
  metadata: {
    processingTimeMs: number;          // Total processing time
    layersUsed: ('rule' | 'embedding' | 'llm')[];  // Layers invoked
  };
}

/**
 * Model type for LLM selection
 */
export type ModelType = 
  | 'qwen-turbo'      // Cheapest, for simple greetings
  | 'qwen-plus'       // Balanced performance
  | 'deepseek-v3'     // Good for reasoning tasks
  | 'deepseek-reasoner' // Specialized for planning
  | 'gpt-4o-mini';    // Fallback option



// ── Constants ──

/**
 * Pre-defined classification rules with patterns and weights
 */
const CLASSIFICATION_RULES: Array<{
  pattern: RegExp;
  intent: IntentType;
  subtype: SubtypeMap[IntentType];
  model: ModelType;
  weight: number;
  description: string;
}> = [
  // Command patterns - highest priority
  {
    pattern: /^(打开 | 启动 | 运行 | 执行)\s+([\w\u4e00-\u9fa5]+)/i,
    intent: 'command',
    subtype: 'system',
    model: 'deepseek-reasoner',
    weight: 1.0,
    description: 'System command execution',
  },
  // Question patterns - fact questions
  {
    pattern: /^(为什么 | 为何 | 怎么 | 怎样 | 什么 | 谁 | 哪里 | 多少)\s.*[?？]$/,
    intent: 'question',
    subtype: 'fact',
    model: 'qwen-plus',
    weight: 0.95,
    description: 'Fact-seeking question',
  },
  // Creative patterns - writing tasks
  {
    pattern: /^(写 | 创作 | 设计 | 编 | 讲)\s+(一 | 个 | 篇)/i,
    intent: 'creative',
    subtype: 'writing',
    model: 'qwen-plus',
    weight: 0.9,
    description: 'Creative writing request',
  },
  // Task patterns - multi-step operations
  {
    pattern: /^(帮 | 为)\s+我\s+(做 | 整理 | 规划 | 分析)/i,
    intent: 'task',
    subtype: 'planning',
    model: 'deepseek-v3',
    weight: 0.85,
    description: 'Multi-step task requirement',
  },
  // Casual greetings - low priority
  {
    pattern: /^(你好 | 嗨 | 早上好 | 晚上好 | 早安 | 晚安|hola|hello|hi)/i,
    intent: 'chat',
    subtype: 'greeting',
    model: 'qwen-turbo',
    weight: 0.98,
    description: 'Casual greeting interaction',
  },
  // Small talk patterns
  {
    pattern: /(今天天气不错 | 心情怎么样 | 最近忙吗|最近在干嘛)/,
    intent: 'chat',
    subtype: 'smalltalk',
    model: 'qwen-turbo',
    weight: 0.92,
    description: 'Small talk engagement',
  },
];

/**
 * Tool detection keywords map
 */
const TOOL_KEYWORDS: Record<string, string[]> = {
  'file-explorer': ['打开文件', '浏览文件夹', 'locate files', '打开资源管理器'],
  'search-browser': ['百度搜索', 'google search', '查找信息', 'google 搜索'],
  'weather-check': ['查询天气', 'check weather', '明天天气', '天气预报'],
  'calendar-view': ['查看日程', 'see calendar', '今天有什么安排', '日历'],
  'system-info': ['系统信息', 'show me info', '电脑配置', '系统状态'],
};

// ── LRU Cache & Performance Monitoring ──

/**
 * Simple LRU cache for classification results to avoid re-computing identical inputs.
 * Keyed by a hash of the text (ignoring context for cache hit simplicity).
 */
const classificationCache = new Map<string, IntentClassification>();
const MAX_CACHE_SIZE = 500;

/**
 * Performance metrics aggregated across all classifyIntent calls.
 * Logged every 100 requests for observability.
 */
const performanceMetrics = {
  totalRequests: 0,
  totalProcessingTimeMs: 0,
  layerHitCounts: { rule: 0, embedding: 0, llm: 0, default: 0 } as Record<string, number>,
  cacheHits: 0,
};

/**
 * Lightweight string hash (djb2 algorithm) for cache keys.
 * Not cryptographically secure but fast and sufficient for dedup.
 */
function hashText(text: string): string {
  let hash = 5381;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) + hash + text.charCodeAt(i)) & 0xffffffff;
  }
  return hash.toString(16);
}

/**
 * Retrieve a cached classification result (LRU eviction: least recently used first).
 */
function getCachedClassification(text: string): IntentClassification | null {
  const key = hashText(text);
  const cached = classificationCache.get(key);
  if (cached) {
    // Move to end (most recently used) by re-inserting
    classificationCache.delete(key);
    classificationCache.set(key, cached);
    performanceMetrics.cacheHits++;
    return { ...cached, id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}` };
  }
  return null;
}

/**
 * Store a classification result in the LRU cache.
 */
function setCachedClassification(text: string, result: IntentClassification): void {
  const key = hashText(text);
  if (classificationCache.size >= MAX_CACHE_SIZE) {
    // Evict least recently used (first entry in Map)
    const firstKey = classificationCache.keys().next().value;
    if (firstKey !== undefined) {
      classificationCache.delete(firstKey);
    }
  }
  classificationCache.set(key, result);
}

/**
 * Log aggregated performance metrics every 100 requests.
 */
function logPerformanceMetrics(): void {
  performanceMetrics.totalRequests++;
  if (performanceMetrics.totalRequests % 100 === 0) {
    const avgTime = performanceMetrics.totalRequests > 0
      ? (performanceMetrics.totalProcessingTimeMs / performanceMetrics.totalRequests).toFixed(2)
      : '0';
    console.log(
      `[IntentClassifier Performance] Requests: ${performanceMetrics.totalRequests}, ` +
      `Avg time: ${avgTime}ms, ` +
      `Layer hits: rule=${performanceMetrics.layerHitCounts.rule}, ` +
      `embedding=${performanceMetrics.layerHitCounts.embedding}, ` +
      `llm=${performanceMetrics.layerHitCounts.llm}, ` +
      `default=${performanceMetrics.layerHitCounts.default}, ` +
      `Cache hits: ${performanceMetrics.cacheHits}`
    );
  }
}

/**
 * Clear the classification cache (useful for testing or memory management).
 */
export function clearClassificationCache(): void {
  classificationCache.clear();
}

/**
 * Get a snapshot of current performance metrics.
 */
export function getPerformanceMetrics() {
  return { ...performanceMetrics };
}

// ── Main Exported Functions ──

/**
 * Classify user input intent using layered approach.
 * Results are cached (LRU) for identical text to skip redundant computation.
 * @param text - User input text to classify
 * @param context - Optional context (userId, conversationHistory)
 * @returns Promise<IntentClassification>
 */
export async function classifyIntent(
  text: string,
  context?: {
    userId?: string;
    conversationHistory?: any[];
  }
): Promise<IntentClassification> {
  const startTime = Date.now();
  const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Check LRU cache first (skip context for cache key simplicity)
  const cached = getCachedClassification(text);
  if (cached) {
    return {
      ...cached,
      id: requestId,
      metadata: {
        ...cached.metadata,
        processingTimeMs: Date.now() - startTime,
        layersUsed: ['cache' as any, ...cached.metadata.layersUsed],
      },
    };
  }

  const layersUsed: string[] = [];

  // Layer 1: Fast Rule Engine
  try {
    const ruleResult = await applyRuleEngine(text);
    if (ruleResult && ruleResult.confidence > 0.85) {
      performanceMetrics.totalProcessingTimeMs += Date.now() - startTime;
      performanceMetrics.layerHitCounts.rule++;
      const result = {
        ...ruleResult,
        originalText: text,
        id: requestId,
        metadata: {
          processingTimeMs: Date.now() - startTime,
          layersUsed: ['rule'] as any,
        },
      } as IntentClassification;
      setCachedClassification(text, result);
      logPerformanceMetrics();
      return result;
    }
    layersUsed.push('rule');
  } catch (error) {
    console.warn('[IntentClassifier] Rule engine fallback:', error);
  }

  // Layer 2: Embedding-based Similarity
  try {
    const embeddingResult = await applyEmbeddingClassifier(text);
    if (embeddingResult && embeddingResult.confidence > 0.60) {
      performanceMetrics.totalProcessingTimeMs += Date.now() - startTime;
      performanceMetrics.layerHitCounts.embedding++;
      const result = {
        ...embeddingResult,
        originalText: text,
        id: requestId,
        metadata: {
          processingTimeMs: Date.now() - startTime,
          layersUsed: [...layersUsed, 'embedding'] as any,
        },
      } as IntentClassification;
      setCachedClassification(text, result);
      logPerformanceMetrics();
      return result;
    }
    layersUsed.push('embedding');
  } catch (error) {
    console.warn('[IntentClassifier] Embedding classifier fallback:', error);
  }

  // Layer 3: LLM Fallback
  try {
    const llmResult = await applyLLMReasoner(text, context);
    if (llmResult) {
      performanceMetrics.totalProcessingTimeMs += Date.now() - startTime;
      performanceMetrics.layerHitCounts.llm++;
      const result = {
        ...llmResult,
        originalText: text,
        id: requestId,
        metadata: {
          processingTimeMs: Date.now() - startTime,
          layersUsed: [...layersUsed, 'llm'] as any,
        },
      } as IntentClassification;
      setCachedClassification(text, result);
      logPerformanceMetrics();
      return result;
    }
  } catch (error) {
    console.warn('[IntentClassifier] LLM fallback failed:', error);
  }

  // Ultimate fallback: default to chat
  const defaultResult = createDefaultClassification(text);
  performanceMetrics.totalProcessingTimeMs += Date.now() - startTime;
  performanceMetrics.layerHitCounts.default++;
  const defaultClass = {
    ...defaultResult,
    originalText: text,
    id: requestId,
    metadata: {
      processingTimeMs: Date.now() - startTime,
      layersUsed: [...layersUsed] as any,
    },
  } as IntentClassification;
  setCachedClassification(text, defaultClass);
  logPerformanceMetrics();
  return defaultClass;
}

/**
 * Apply Rule Engine layer
 */
async function applyRuleEngine(text: string): Promise<Omit<IntentClassification, 'id' | 'metadata' | 'originalText'> | null> {
  for (const rule of CLASSIFICATION_RULES) {
    const match = text.match(rule.pattern);
    if (match) {
      const confidence = calculateRuleConfidence(match, text, rule.weight);
      
      return createClassification({
        intent: rule.intent,
        subtype: rule.subtype,
        confidence,
        suggestedModel: rule.model,
        priority: determinePriority(rule.intent, confidence),
        reasoning: `Matched rule: ${rule.description}`,
        entities: extractEntities(text),
        requiresTools: detectTools(text),
      });
    }
  }
  
  throw new Error('No matching rule found');
}

/**
 * Detect required tools from text
 */
export function detectTools(text: string): string[] {
  const detected: string[] = [];
  
  for (const [toolName, keywords] of Object.entries(TOOL_KEYWORDS)) {
    if (keywords.some(keyword => text.includes(keyword))) {
      detected.push(toolName);
    }
  }
  
  return detected;
}

/**
 * Calculate confidence based on regex match quality
 */
function calculateRuleConfidence(
  match: RegExpMatchArray,
  text: string,
  baseWeight: number
): number {
  let score = baseWeight;
  
  // Bonus for exact keyword matches (capturing groups)
  if (match.length >= 2) score += 0.05;
  if (match.length >= 3) score += 0.05;
  
  // Penalty for partial/fuzzy matches
  if (match.index !== 0) score -= 0.02;
  
  // Bonus for complete sentences
  if (isCompleteSentence(text)) score += 0.03;
  
  return Math.min(score, 0.99);
}

/**
 * Determine response priority based on intent and confidence
 */
function determinePriority(intent: IntentType, confidence?: number): Priority {
  if (intent === 'command' && confidence && confidence > 0.9) return 'urgent';
  if (intent === 'task') return 'high';
  return 'normal';
}

/**
 * Extract key entities from text
 */
function extractEntities(text: string): Entity[] {
  // Simple entity extraction - expand later
  const entities: Entity[] = [];
  
  // Number patterns
  const numbers = text.match(/\d+(\.\d+)?/g);
  if (numbers) {
    numbers.forEach(num => {
      entities.push({ name: 'number', value: num });
    });
  }
  
  // URL patterns
  const urls = text.match(/https?:\/\/[^\s]+/g);
  if (urls) {
    urls.forEach(url => {
      entities.push({ name: 'url', value: url });
    });
  }
  
  // Email patterns
  const emails = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g);
  if (emails) {
    emails.forEach(email => {
      entities.push({ name: 'email', value: email });
    });
  }
  
  return entities;
}

/**
 * Check if text is a complete sentence
 */
function isCompleteSentence(text: string): boolean {
  return /[.!??.！！？]$/.test(text.trim());
}

/**
 * Create IntentClassification object
 */
function createClassification(
  data: Omit<IntentClassification, 'id' | 'metadata' | 'originalText'>
): Omit<IntentClassification, 'id' | 'metadata' | 'originalText'> {
  return data;
}

// ── Layer 2: Embedding-based Similarity Classifier (placeholder for Phase 2) ──

/**
 * Intent prototypes mapped to embedding vectors
 * Format: { intentType: [prototype_phrases] }
 */
const INTENT_PROTOTYPES = new Map<IntentType, string[]>([
  ['chat', ["你好", "嗨", "早上好", "good morning", "hello", "随便聊聊", "最近怎么样"]],
  ['question', ['为什么', '为何', '怎么', '怎样', '什么', '谁', '哪里', '多少', 'explain', 'why']],
  ['task', ['帮我', '为', '做', '整理', '规划', '分析', 'organize', 'plan']],
  ['creative', ['写', '创作', '设计', '编', '讲', '写一篇', 'design a', 'write a']],
  ['command', ['打开', '启动', '运行', '执行', 'open', 'run', 'execute']],
]);

/**
 * Lazy-initialized encoder for semantic embeddings
 */
let textEncoder: any | null = null;
let encoderInitializing = false;
let encoderPromise: Promise<any> | null = null;

async function initTextEncoder(): Promise<typeof import('@xenova/transformers')> {
  if (textEncoder) return Promise.resolve(textEncoder);
  
  if (!encoderInitializing) {
    encoderInitializing = true;
    encoderPromise = (async () => {
      try {
        const transformers = await import('@xenova/transformers');
        textEncoder = transformers;
        console.log('[IntentClassifier] Text encoder initialized successfully');
        return transformers;
      } catch (error) {
        console.error('[IntentClassifier] Failed to initialize encoder:', error);
        throw error;
      }
    })();
  }
  
  return encoderPromise || Promise.reject(new Error('Encoder initialization failed'));
}

/**
 * Compute average embedding from multiple example phrases
 */
async function computePrototypeEmbedding(phrases: string[]): Promise<number[]> {
  const encoder = await initTextEncoder();
  const model = await encoder.pipeline('feature-extraction', 'Xenova/all-MiniLM-L6-v2');
  
  let aggregatedEmbedding: number[] | null = null;
  let count = 0;
  
  for (const phrase of phrases.slice(0, 5)) { // Limit to 5 phrases per prototype
    const result = await model(phrase, { pooling: 'mean', normalize: true });
    const embedding = Array.from(result.data as Float32Array);
    
    if (!aggregatedEmbedding) {
      aggregatedEmbedding = embedding;
    } else {
      for (let i = 0; i < embedding.length; i++) {
        aggregatedEmbedding[i] += embedding[i];
      }
    }
    count++;
  }
  
  // Average the embeddings
  if (aggregatedEmbedding && count > 0) {
    aggregatedEmbedding = aggregatedEmbedding.map(v => v / count);
  }
  
  await model.dispose();
  return aggregatedEmbedding || new Array(384).fill(0); // Default dimension for all-MiniLM-L6-v2
}

/**
 * Cosine similarity between two vectors
 */
function cosineSimilarity(a: number[], b: number[]): number {
  let dotProduct = 0;
  let magnitudeA = 0;
  let magnitudeB = 0;
  
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    dotProduct += a[i] * (b[i] || 0);
    magnitudeA += a[i] * a[i];
    magnitudeB += (b[i] || 0) * (b[i] || 0);
  }
  
  const denominator = Math.sqrt(magnitudeA) * Math.sqrt(magnitudeB);
  return denominator === 0 ? 0 : dotProduct / denominator;
}

/**
 * Apply Embedding-based Similarity Classification (Layer 2)
 */
export async function applyEmbeddingClassifier(
  text: string,
  confidenceThreshold: number = 0.6
): Promise<Omit<IntentClassification, 'id' | 'metadata' | 'originalText'> | null> {
  try {
    const encoderModule = await initTextEncoder();
    // Use pipeline for feature extraction instead of direct encoding
    const embedder = await encoderModule.pipeline('feature-extraction', 'Xenova/all-MiniLM-L6-v2');
    const inputEmbedding = await embedder(text, { pooling: 'mean', normalize: true });
    const inputVector = Array.from(inputEmbedding.data as Float32Array);
    
    let bestMatch: { intent: IntentType; similarity: number } | null = null;
    
    // Compute similarity against all intent prototypes
    for (const [intentType, examples] of INTENT_PROTOTYPES.entries()) {
      const prototypeEmbedding = await computePrototypeEmbedding(examples);
      
      const similarity = cosineSimilarity(Array.from(inputVector), prototypeEmbedding);
      
      if (!bestMatch || similarity > bestMatch.similarity) {
        bestMatch = { intent: intentType, similarity };
      }
    }
    
    if (bestMatch && bestMatch.similarity >= confidenceThreshold) {
      const adjustedConfidence = calculateEmbeddingConfidence(bestMatch.similarity);
      
      return createClassification({
        intent: bestMatch.intent,
        subtype: mapIntentToSubtype(bestMatch.intent),
        confidence: adjustedConfidence,
        suggestedModel: inferModelFromIntent(bestMatch.intent),
        priority: 'normal',
        reasoning: `Embedding similarity: ${adjustedConfidence.toFixed(3)}`,
        entities: [],
        requiresTools: [],
      });
    }
    
    return null;
  } catch (error) {
    console.warn('[IntentClassifier] Embedding classifier failed:', error);
    return null;
  }
}

function calculateEmbeddingConfidence(similarity: number): number {
  // Threshold-based adjustment
  if (similarity > 0.85) return similarity + 0.05;
  if (similarity > 0.70) return similarity;
  if (similarity > 0.50) return similarity - 0.10;
  return similarity - 0.20;
}

function mapIntentToSubtype(intent: IntentType): SubtypeMap[IntentType] {
  switch (intent) {
    case 'chat': return 'casual';
    case 'question': return 'fact';
    case 'task': return 'planning';
    case 'creative': return 'writing';
    case 'command': return 'system';
  }
}

function inferModelFromIntent(intent: IntentType): ModelType {
  switch (intent) {
    case 'chat': return 'qwen-turbo';
    case 'question': return 'qwen-plus';
    case 'task': return 'deepseek-v3';
    case 'creative': return 'qwen-plus';
    case 'command': return 'deepseek-reasoner';
  }
}

// ── Layer 3: LLM-based Reasoning Fallback (for complex queries) ──

/**
 * System prompt for intent classification via LLM
 */
const INTENT_CLASSIFICATION_PROMPT = `
You are an expert intent classifier for the Xiaofeng OS dialogue system.
Analyze the user's input and classify it into one of these intent types:
- chat: Casual conversation, greetings, small talk
- question: Fact-seeking questions (why/what/how/who)
- task: Multi-step operations requiring planning or analysis
- creative: Writing, design, brainstorming requests
- command: Direct system commands (open/run/execute)

For each input, also identify:
1. Subtype within the main category
2. Confidence score (0.0-1.0)
3. Suggested LLM model based on complexity
4. Priority level (urgent/high/normal)
5. Any entities mentioned
6. Tools that might be required

Return ONLY a JSON object in this format:
{
  "intent": "chat/task/question/creative/command",
  "subtype": "specific subtype",
  "confidence": 0.0-1.0,
  "reasoning": "brief explanation",
  "suggestedModel": "model name",
  "priority": "urgent/high/normal",
  "entities": ["list", "of", "entities"],
  "requiresTools": ["tool", "names"]
}
`;

/**
 * Apply LLM-based Classification Fallback with 10s explicit timeout protection.
 * Used when Rule Engine and Embedding Classifier both fail to reach confidence threshold.
 * If the LLM call exceeds 10 seconds, it aborts and returns null (triggering default classification).
 */
const LLM_FALLBACK_TIMEOUT_MS = 10_000;

export async function applyLLMReasoner(
  text: string,
  context?: {
    userId?: string;
    conversationHistory?: any[];
  }
): Promise<Omit<IntentClassification, 'id' | 'metadata'> | null> {
  const startTime = Date.now();
  
  try {
    const apiFetch = (await import('../services/apiClient')).apiFetch;
    
    // Construct context-aware prompt
    let fullPrompt = INTENT_CLASSIFICATION_PROMPT;
    if (context?.conversationHistory && context.conversationHistory.length > 0) {
      const lastMsg = context.conversationHistory[context.conversationHistory.length - 1];
      fullPrompt += `\n\nConversation Context:\nUser: ${lastMsg.text || ''}\nAssistant: ${lastMsg.text || ''}\n`;
    }
    fullPrompt += `\n\nTarget Input: "${text}"`;
    
    // Create a timeout promise that rejects after LLM_FALLBACK_TIMEOUT_MS
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`LLM fallback timeout after ${LLM_FALLBACK_TIMEOUT_MS}ms`));
      }, LLM_FALLBACK_TIMEOUT_MS);
    });
    
    // Race the fetch against the timeout
    const response = await Promise.race([
      apiFetch('/api/llm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          prompt: fullPrompt,
          model: 'qwen-plus', // Use balanced model for classification
          temperature: 0.1,   // Low temperature for consistency
          maxTokens: 200,
        }),
      }),
      timeoutPromise,
    ]);
    
    if (!response.ok) throw new Error('LLM service unavailable');
    const data = await response.json();
    
    // Parse the classification result from LLM response
    const textContent = typeof data === 'string' ? data : String(data.text || data.response || '');
    const jsonMatch = textContent.match(/\{[^{}]*\}/);
    
    if (!jsonMatch) {
      console.warn('[IntentClassifier] LLM response not JSON:', textContent);
      return null;
    }
    
    const classified = JSON.parse(jsonMatch[0]);
    
    // Validate and enhance with context
    if (['chat', 'task', 'question', 'creative', 'command'].includes(classified.intent)) {
      return {
        ...createClassification({
          intent: classified.intent as IntentType,
          subtype: classified.subtype || mapIntentToSubtype(classified.intent),
          confidence: Math.min(Math.max(classified.confidence || 0.5, 0.0), 1.0),
          suggestedModel: classified.suggestedModel || inferModelFromIntent(classified.intent),
          priority: classified.priority || 'normal',
          reasoning: classified.reasoning || 'LLM-based classification',
          entities: Array.isArray(classified.entities) ? classified.entities.map((e: string) => ({ name: e })) : [],
          requiresTools: Array.isArray(classified.requiresTools) ? classified.requiresTools : [],
        }),
        originalText: text,
      };
    }
    
    return null;
  } catch (error) {
    const elapsed = Date.now() - startTime;
    if (error instanceof Error && error.message.includes('timeout')) {
      console.warn(`[IntentClassifier] LLM fallback timed out after ${elapsed}ms, falling back to default classification`);
    } else {
      console.error(`[IntentClassifier] LLM fallback failed after ${elapsed}ms:`, error);
    }
    return null;
  }
}

/**
 * Create default classification when all layers fail
 */
function createDefaultClassification(text: string): Omit<IntentClassification, 'id' | 'metadata'> {
  return {
    originalText: text,
    intent: 'chat',
    subtype: 'casual',
    confidence: 0.3,
    reasoning: 'Default classification due to no successful matches',
    suggestedModel: 'qwen-turbo',
    priority: 'normal',
    entities: extractEntities(text),
    requiresTools: detectTools(text),
  };
}
